import { LinkText, Paragraph } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { DaycareGoal } from '@common/goals';
import {
    isUsaRegionCode,
    UsaRegionCode,
    usaRegionNameByRegionCode,
} from '@common/location';
import { getGoalBaseConfig } from '@common/react/goals';
import {
    DaycareAgeLevel,
    DaycareCostInfo,
    DaycareQuality,
} from '@common/types';
import { dt, isPastDate } from '@common/util/dateTime';
import { capitalize } from '@common/util/strings';
import { isEmpty } from '@common/util/types';
import isEqual from 'lodash.isequal';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

const qualityLabelByValue: Record<DaycareQuality, string> = {
    STANDARD: 'Average',
    HIGHER: 'Above average',
} as const;

const ageLevelLabelByValue: Record<DaycareAgeLevel, string> = {
    INFANT: '0-1 year old',
    TODDLER: '2-3 years old',
    PRESCHOOLER: '4-5 years old',
} as const;

type Country = 'United States';

const costByUsaRegionCodeByQualityByAgeLevel: Record<
    DaycareAgeLevel,
    Record<DaycareQuality, Record<UsaRegionCode, number>>
> = {
    INFANT: {
        STANDARD: {
            AL: 12_400_00,
            AK: 17_300_00,
            AZ: 15_400_00,
            AR: 13_800_00,
            CA: 21_400_00,
            CO: 17_400_00,
            CT: 20_500_00,
            DE: 16_800_00,
            DC: 24_500_00,
            FL: 16_400_00,
            GA: 12_200_00,
            HI: 20_300_00,
            ID: 12_000_00,
            IL: 14_200_00,
            IN: 15_700_00,
            IA: 16_500_00,
            KS: 15_500_00,
            KY: 14_300_00,
            LA: 10_000_00,
            ME: 19_500_00,
            MD: 24_500_00,
            MA: 24_100_00,
            MI: 13_600_00,
            MN: 19_900_00,
            MS: 13_200_00,
            MO: 17_400_00,
            MT: 12_900_00,
            NE: 14_300_00,
            NV: 13_300_00,
            NH: 13_800_00,
            NJ: 17_500_00,
            NM: 12_700_00,
            NY: 22_500_00,
            NC: 13_900_00,
            ND: 13_700_00,
            OH: 12_700_00,
            OK: 15_800_00,
            OR: 19_000_00,
            PA: 17_200_00,
            RI: 19_500_00,
            SC: 13_600_00,
            SD: 9_700_00,
            TN: 16_800_00,
            TX: 15_000_00,
            UT: 16_600_00,
            VT: 20_900_00,
            VA: 19_000_00,
            WA: 20_500_00,
            WV: 16_400_00,
            WI: 16_600_00,
            WY: 15_000_00,
        },
        HIGHER: {
            AL: 24_700_00,
            AK: 38_300_00,
            AZ: 25_200_00,
            AR: 23_400_00,
            CA: 37_300_00,
            CO: 29_000_00,
            CT: 40_900_00,
            DE: 32_400_00,
            DC: 31_200_00,
            FL: 26_900_00,
            GA: 27_000_00,
            HI: 25_700_00,
            ID: 24_000_00,
            IL: 33_600_00,
            IN: 24_900_00,
            IA: 26_400_00,
            KS: 32_000_00,
            KY: 27_800_00,
            LA: 23_800_00,
            ME: 30_100_00,
            MD: 39_100_00,
            MA: 41_100_00,
            MI: 27_200_00,
            MN: 30_000_00,
            MS: 22_500_00,
            MO: 27_100_00,
            MT: 25_900_00,
            NE: 27_500_00,
            NV: 31_000_00,
            NH: 31_500_00,
            NJ: 35_000_00,
            NM: 25_900_00,
            NY: 35_400_00,
            NC: 25_000_00,
            ND: 29_700_00,
            OH: 28_000_00,
            OK: 23_300_00,
            OR: 36_200_00,
            PA: 31_200_00,
            RI: 39_600_00,
            SC: 22_900_00,
            SD: 23_200_00,
            TN: 24_800_00,
            TX: 24_900_00,
            UT: 27_600_00,
            VT: 29_800_00,
            VA: 30_700_00,
            WA: 35_100_00,
            WV: 27_500_00,
            WI: 28_600_00,
            WY: 29_300_00,
        },
    },
    TODDLER: {
        STANDARD: {
            AL: 9_800_00,
            AK: 15_600_00,
            AZ: 11_800_00,
            AR: 10_600_00,
            CA: 16_700_00,
            CO: 14_300_00,
            CT: 20_500_00,
            DE: 11_700_00,
            DC: 24_500_00,
            FL: 9_500_00,
            GA: 9_400_00,
            HI: 15_900_00,
            ID: 10_400_00,
            IL: 12_200_00,
            IN: 11_500_00,
            IA: 13_100_00,
            KS: 11_900_00,
            KY: 9_800_00,
            LA: 8_300_00,
            ME: 16_900_00,
            MD: 15_700_00,
            MA: 20_400_00,
            MI: 11_700_00,
            MN: 14_300_00,
            MS: 11_000_00,
            MO: 11_700_00,
            MT: 11_100_00,
            NE: 14_300_00,
            NV: 10_900_00,
            NH: 11_300_00,
            NJ: 17_500_00,
            NM: 9_800_00,
            NY: 17_600_00,
            NC: 12_500_00,
            ND: 10_900_00,
            OH: 11_700_00,
            OK: 10_600_00,
            OR: 16_500_00,
            PA: 13_700_00,
            RI: 15_400_00,
            SC: 10_600_00,
            SD: 9_700_00,
            TN: 13_200_00,
            TX: 9_800_00,
            UT: 12_000_00,
            VT: 18_100_00,
            VA: 12_800_00,
            WA: 14_700_00,
            WV: 11_000_00,
            WI: 13_200_00,
            WY: 15_000_00,
        },
        HIGHER: {
            AL: 19_500_00,
            AK: 29_400_00,
            AZ: 19_500_00,
            AR: 18_100_00,
            CA: 28_700_00,
            CO: 22_400_00,
            CT: 40_900_00,
            DE: 25_300_00,
            DC: 31_200_00,
            FL: 21_000_00,
            GA: 20_900_00,
            HI: 19_900_00,
            ID: 18_800_00,
            IL: 25_900_00,
            IN: 19_400_00,
            IA: 20_600_00,
            KS: 20_000_00,
            KY: 21_200_00,
            LA: 18_400_00,
            ME: 25_900_00,
            MD: 24_300_00,
            MA: 34_500_00,
            MI: 21_000_00,
            MN: 23_100_00,
            MS: 17_500_00,
            MO: 21_000_00,
            MT: 20_100_00,
            NE: 21_200_00,
            NV: 24_100_00,
            NH: 24_400_00,
            NJ: 27_200_00,
            NM: 20_000_00,
            NY: 27_600_00,
            NC: 19_400_00,
            ND: 23_000_00,
            OH: 21_700_00,
            OK: 18_100_00,
            OR: 31_200_00,
            PA: 24_300_00,
            RI: 30_600_00,
            SC: 17_900_00,
            SD: 18_100_00,
            TN: 19_200_00,
            TX: 19_300_00,
            UT: 21_500_00,
            VT: 25_700_00,
            VA: 23_700_00,
            WA: 27_100_00,
            WV: 21_000_00,
            WI: 22_300_00,
            WY: 25_400_00,
        },
    },
    PRESCHOOLER: {
        STANDARD: {
            AL: 8_600_00,
            AK: 12_200_00,
            AZ: 9_400_00,
            AR: 8_800_00,
            CA: 11_900_00,
            CO: 12_000_00,
            CT: 12_700_00,
            DE: 10_700_00,
            DC: 16_500_00,
            FL: 8_400_00,
            GA: 8_000_00,
            HI: 11_500_00,
            ID: 8_700_00,
            IL: 11_000_00,
            IN: 9_800_00,
            IA: 11_400_00,
            KS: 9_300_00,
            KY: 9_000_00,
            LA: 7_400_00,
            ME: 11_700_00,
            MD: 12_200_00,
            MA: 13_200_00,
            MI: 10_500_00,
            MN: 12_000_00,
            MS: 9_300_00,
            MO: 10_600_00,
            MT: 9_300_00,
            NE: 11_100_00,
            NV: 9_700_00,
            NH: 10_100_00,
            NJ: 13_700_00,
            NM: 9_000_00,
            NY: 13_800_00,
            NC: 8_800_00,
            ND: 8_100_00,
            OH: 9_200_00,
            OK: 8_900_00,
            OR: 11_600_00,
            PA: 10_800_00,
            RI: 12_600_00,
            SC: 8_900_00,
            SD: 9_700_00,
            TN: 10_800_00,
            TX: 8_600_00,
            UT: 9_500_00,
            VT: 12_500_00,
            VA: 11_600_00,
            WA: 12_400_00,
            WV: 9_900_00,
            WI: 10_400_00,
            WY: 9_800_00,
        },
        HIGHER: {
            AL: 15_400_00,
            AK: 22_300_00,
            AZ: 15_000_00,
            AR: 13_900_00,
            CA: 21_800_00,
            CO: 17_100_00,
            CT: 24_000_00,
            DE: 19_700_00,
            DC: 20_800_00,
            FL: 16_300_00,
            GA: 16_000_00,
            HI: 15_200_00,
            ID: 14_600_00,
            IL: 19_800_00,
            IN: 15_000_00,
            IA: 17_800_00,
            KS: 15_100_00,
            KY: 15_900_00,
            LA: 14_100_00,
            ME: 17_300_00,
            MD: 18_400_00,
            MA: 21_900_00,
            MI: 16_100_00,
            MN: 17_600_00,
            MS: 13_600_00,
            MO: 16_200_00,
            MT: 15_500_00,
            NE: 16_100_00,
            NV: 18_600_00,
            NH: 18_800_00,
            NJ: 21_000_00,
            NM: 15_400_00,
            NY: 21_300_00,
            NC: 14_900_00,
            ND: 17_600_00,
            OH: 16_700_00,
            OK: 13_900_00,
            OR: 21_300_00,
            PA: 18_800_00,
            RI: 24_600_00,
            SC: 13_900_00,
            SD: 14_000_00,
            TN: 15_500_00,
            TX: 14_800_00,
            UT: 16_700_00,
            VT: 17_400_00,
            VA: 18_200_00,
            WA: 20_600_00,
            WV: 15_700_00,
            WI: 17_200_00,
            WY: 17_500_00,
        },
    },
};

const costByCountryByQualityByAgeLevel: Record<
    DaycareAgeLevel,
    Record<DaycareQuality, Record<Country, number>>
> = {
    INFANT: {
        STANDARD: {
            'United States': 15_900_00,
        },
        HIGHER: {
            'United States': 28_800_00,
        },
    },
    TODDLER: {
        STANDARD: {
            'United States': 13_200_00,
        },
        HIGHER: {
            'United States': 22_400_00,
        },
    },
    PRESCHOOLER: {
        STANDARD: {
            'United States': 10_700_00,
        },
        HIGHER: {
            'United States': 17_200_00,
        },
    },
};

export const DAYCARE: GoalConfig<DaycareGoal> = {
    inputSteps: [
        {
            id: 'quality',
            title: 'What kind of childcare do you want to plan for?',
            fieldConfigs: [
                {
                    fieldName: 'quality',
                    fieldType: 'radioCards',
                    fieldOptions: Object.entries(qualityLabelByValue).map(
                        ([value, label]) => ({ value, label })
                    ),
                },
            ],
        },
        {
            id: 'ageLevel',
            title: 'How old will your child be?',
            fieldConfigs: [
                {
                    fieldName: 'ageLevel',
                    fieldType: 'select',
                    fieldOptions: Object.entries(ageLevelLabelByValue).map(
                        ([value, label]) => ({ value, label })
                    ),
                    fieldValueDefault: 'INFANT',
                },
            ],
        },
        {
            id: 'yearMonth',
            title: 'Roughly, when do you want these daycare savings by?',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            isNextButtonDisabled: (goalData) =>
                !goalData.targetDate || isPastDate(goalData.targetDate),
        },
        {
            id: 'targetAmount',
            title: 'Choose a target daycare cost that you feel comfortable with.',
            fieldConfigs: [
                {
                    fieldName: 'targetAmount',
                    fieldType: 'currency',
                    min: 0,
                    slider: ({ goalData }) => {
                        return goalData.projectedAmountInfo?.amount
                            ? {
                                  min: 0,
                                  max: goalData.projectedAmountInfo.amount * 2,
                                  step: 1_000_00, // $1,000
                              }
                            : {
                                  min: 0,
                                  max: 100_000_00, // $100,000
                                  step: 1_000_00, // $1,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) => {
                        const { quality, ageLevel, projectedAmountInfo } =
                            goalData;

                        if (!quality || !ageLevel || !projectedAmountInfo) {
                            return undefined;
                        }

                        return (
                            <>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={8}
                                >{`1 year of ${qualityLabelByValue[
                                    quality
                                ].toLowerCase()} childcare for a ${ageLevelLabelByValue[
                                    ageLevel
                                ].replace('years', 'year')} in ${
                                    projectedAmountInfo.locationName
                                } averages ${toDollars(
                                    projectedAmountInfo.amount
                                )}.`}</Paragraph>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mt={3}
                                    onClick={() => {
                                        showSlideup('targetAmountAssumptions');
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </>
                        );
                    },
                },
            ],
        },
    ],
    assumptions: [
        {
            id: 'targetAmountAssumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Your estimated daycare expenses are calculated using:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            State averages for different qualities of childcare,
                            courtesy of the Center for American Progress
                        </ListItem>
                    </UnorderedList>
                    <Paragraph fontSize="sm" lineHeight="tall" mt={4}>
                        Note: Each childcare provider may have additional
                        inclusions and locations within a state will vary. We’ve
                        used state average as the best initial estimate. If you
                        are actively planning for daycare, we recommend you talk
                        to a shortlist of local providers for the most accurate
                        price estimates.
                    </Paragraph>
                </>
            ),
        },
    ],

    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (isEmpty(goalData.targetAmount) || isEmpty(goalData.targetDate)) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = DAYCARE.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() => onGoalStatementClick('targetAmount')}
                    color={color}
                />
                {' for daycare expenses by '}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {'.'}
            </>
        );
    },
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
        userInfo,
    }): Promise<GoalData<DaycareGoal>> => {
        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedAmountInfo = getProjectedAmountInfo({
            quality: newGoalData.quality,
            ageLevel: newGoalData.ageLevel,
            regionCode: userInfo.region,
        });

        return {
            ...newGoalData,
            targetDate,
            projectedAmountInfo,

            // update targetAmount if projected amount changed using formula above
            ...(!isEqual(
                oldGoalData.projectedAmountInfo,
                projectedAmountInfo
            ) && {
                targetAmount: projectedAmountInfo?.amount,
            }),
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('DAYCARE'),
};

const getProjectedAmountInfo = ({
    quality,
    ageLevel,
    regionCode,
}: {
    quality?: DaycareQuality | null;
    ageLevel?: DaycareAgeLevel | null;
    regionCode?: UsaRegionCode | string | null;
}): DaycareCostInfo | undefined => {
    if (isEmpty(quality) || isEmpty(ageLevel)) return undefined;

    if (isUsaRegionCode(regionCode)) {
        const amount =
            costByUsaRegionCodeByQualityByAgeLevel[ageLevel][quality][
                regionCode
            ];
        return {
            locationName: usaRegionNameByRegionCode[regionCode],
            amount,
        };
    } else {
        const amount =
            costByCountryByQualityByAgeLevel[ageLevel][quality][
                'United States'
            ];
        return {
            locationName: 'United States',
            amount,
        };
    }
};
